import { customAlertApiRef } from '@agilelab/plugin-wb-platform';
import {
  GetRolesResponse,
  RoleEntity,
  rbacRoleCreatePermission,
  rbacRoleDeletePermission,
} from '@agilelab/plugin-wb-rbac-common';
import {
  configApiRef,
  identityApiRef,
  useApi,
} from '@mainstage/core-plugin-api';
import { usePermission } from '@mainstage/plugin-permission-react';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import useAsyncFn, { AsyncState } from 'react-use/lib/useAsyncFn';
import { rbacApiRef } from '../../..';
import { adaptRole } from '../../../utils';
import { RoleFormType } from '../RoleForm';

export interface CloneRoleDefaults extends RoleFormType {
  sourceRoleId: string;
}

export interface Filters {
  text?: string;
}

export interface RolesTableContextProviderProps {
  children?: ReactNode;
}

export interface RolesTableContextType extends RolesTableContextProviderProps {
  openDrawer: boolean;
  setOpenDrawer: Dispatch<SetStateAction<boolean>>;

  openConfirmDialog: boolean;
  setOpenConfirmDialog: Dispatch<SetStateAction<boolean>>;

  error: Error | undefined;
  setError: Dispatch<SetStateAction<Error | undefined>>;

  canAddNewRolePermission: boolean;
  addRole: (form: RoleFormType) => Promise<RoleEntity[]>;
  addRoleState: AsyncState<RoleEntity[] | undefined>;

  canDeleteNewRolePermission: boolean;
  deleteRoles: (roles: string[]) => Promise<void>;

  loadRoles: () => Promise<GetRolesResponse>;
  rolesLoaded: AsyncState<GetRolesResponse | undefined>;

  filters: Filters;
  setFilters: Dispatch<SetStateAction<Filters>>;

  pagination: {
    limit: number;
    offset: number;
  };
  setPagination: Dispatch<
    SetStateAction<{
      limit: number;
      offset: number;
    }>
  >;

  selectedDeleteItems: RoleEntity[];
  setSelectedDeleteItems: Dispatch<SetStateAction<RoleEntity[]>>;

  selectedRow: RoleEntity | undefined;
  setSelectedRow: Dispatch<SetStateAction<RoleEntity | undefined>>;

  cloneDefaults: CloneRoleDefaults | undefined;
  setCloneDefaults: Dispatch<SetStateAction<CloneRoleDefaults | undefined>>;
  cloneRole: (form: RoleFormType) => Promise<RoleEntity[]>;
  cloneRoleState: AsyncState<RoleEntity[] | undefined>;

  checkIfDisabled: (row: RoleEntity) => boolean;
}

export const RolesTableContext = createContext<RolesTableContextType>(
  {} as RolesTableContextType,
);

export const RolesTableContextProvider = ({
  children,
}: RolesTableContextProviderProps) => {
  const rbacApi = useApi(rbacApiRef);
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  const alertApi = useApi(customAlertApiRef);

  // check if permission check is enabled
  const permissionsEnabled =
    configApi.getOptionalBoolean('permission.enabled') ?? false;

  const createPermissionAllowed = usePermission({
    permission: rbacRoleCreatePermission,
  }).allowed;

  const deletePermissionAllowed = usePermission({
    permission: rbacRoleDeletePermission,
  }).allowed;

  const canAddNewRolePermission =
    !permissionsEnabled || createPermissionAllowed;

  const canDeleteNewRolePermission =
    !permissionsEnabled || deletePermissionAllowed;

  const [error, setError] = useState<Error>();

  const [filters, setFilters] = useState<Filters>({});

  const [openDrawer, setOpenDrawer] = useState(false);

  const [cloneDefaults, setCloneDefaults] = useState<
    CloneRoleDefaults | undefined
  >(undefined);

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const [selectedRow, setSelectedRow] = useState<RoleEntity>();

  const [selectedDeleteItems, setSelectedDeleteItems] = useState<RoleEntity[]>(
    [],
  );

  const checkIfDisabled = useCallback((row: RoleEntity) => {
    return row.visibility === 'internal';
  }, []);

  const [pagination, setPagination] = useState({
    limit: 25,
    offset: 0,
  });

  const [rolesLoaded, loadRoles] = useAsyncFn(async () => {
    const fetchRoles = async (): Promise<GetRolesResponse> => {
      const res = await rbacApi.getRoles({
        searchKeyword: filters.text,

        options: {
          offset: pagination.offset,
          limit: pagination.limit,
          token: (await identityApi.getCredentials()).token,
        },
      });
      return {
        ...res,
        roles: res.roles.map(role => ({
          displayName: role.displayName ?? role.id,
          description: role.description,
          visibility: role.visibility,
          id: role.id,
        })),
      };
    };
    return fetchRoles();
  }, [pagination, filters]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const [addRoleState, addRole] = useAsyncFn(async (form: RoleFormType) => {
    try {
      await rbacApi.addRoles({
        roles: [adaptRole(form)],
        options: { token: (await identityApi.getCredentials()).token },
      });
      setError(undefined);
      setOpenDrawer(false);

      alertApi.post({
        message: `New role successfully created.`,
        severity: 'success',
      });

      loadRoles();
    } catch (e) {
      const message =
        (e as { body?: { error?: { message?: string } } })?.body?.error
          ?.message ?? (e instanceof Error ? e.message : String(e));
      setError(new Error(message));
    }
    return [];
  }, []);

  useEffect(() => {
    if (addRoleState.error) setError(addRoleState.error);
  }, [addRoleState.error]);

  const [cloneRoleState, cloneRole] = useAsyncFn(
    async (form: RoleFormType) => {
      const sourceRoleId = cloneDefaults?.sourceRoleId;
      if (!sourceRoleId) return [];

      try {
        const token = (await identityApi.getCredentials()).token;

        await rbacApi.cloneRole({
          roleId: sourceRoleId,
          displayName: form.displayName,
          description: form.description,
          cloneAssignments: form.cloneAssignments,
          options: { token },
        });

        setError(undefined);
        setCloneDefaults(undefined);

        alertApi.post({
          message: `Role successfully cloned.`,
          severity: 'success',
        });

        loadRoles();
      } catch (e) {
        const message =
          (e as { body?: { error?: { message?: string } } })?.body?.error
            ?.message ?? (e instanceof Error ? e.message : String(e));
        setError(new Error(message));
      }
      return [];
    },
    [cloneDefaults],
  );

  useEffect(() => {
    if (cloneRoleState.error) setError(cloneRoleState.error);
  }, [cloneRoleState.error]);

  const deleteRoles = async (roles: string[]) => {
    try {
      const response = await rbacApi.deleteUserRoles({
        roles,
        options: { token: (await identityApi.getCredentials()).token },
      });

      const { deletedRoles, errors } = response;

      if (deletedRoles.length > 0) {
        alertApi.post({
          message: `Successfully deleted ${deletedRoles.length} ${
            deletedRoles.length === 1 ? 'role' : 'roles'
          }.`,
          severity: 'success',
        });
      }

      if (errors.length > 0) {
        alertApi.post({
          message: `Failed to delete role(s): ${errors.join(', ')}`,
          severity: 'error',
        });
      }

      loadRoles();
    } catch (e) {
      alertApi.post({
        message: `Unexpected error: ${String(e)}`,
        severity: 'error',
      });
    }
  };

  return (
    <RolesTableContext.Provider
      value={{
        checkIfDisabled,

        loadRoles,
        rolesLoaded,

        error,
        setError,

        openDrawer,
        setOpenDrawer,

        openConfirmDialog,
        setOpenConfirmDialog,

        canAddNewRolePermission,
        addRoleState,
        addRole,

        canDeleteNewRolePermission,
        deleteRoles,

        filters,
        setFilters,

        pagination,
        setPagination,

        selectedDeleteItems,
        setSelectedDeleteItems,

        selectedRow,
        setSelectedRow,

        cloneDefaults,
        setCloneDefaults,
        cloneRole,
        cloneRoleState,
      }}
    >
      {children}
    </RolesTableContext.Provider>
  );
};

export const useRolesTableContext = () => {
  const context = useContext(RolesTableContext);
  if (!context) {
    throw new Error(
      'useRolesTableContext must be used within a RolesTableContextProvider',
    );
  }
  return context as RolesTableContextType;
};
